/**
 * Hub's binding of the shared READ-LAYER INHERITANCE of a company's shared
 * contact methods.
 *
 * The resolution itself lives in
 * `@west-tennessee-consulting/contact-management/inherited-methods` — it is pure,
 * and the detail header, the Details tab, the detail modal's info panel and every
 * native client must agree on what a person inherits, so it cannot live in one
 * app. Read that module for the model (an inherited method carries
 * `scope: "company"`, is never editable, and an anchor inherits nothing).
 *
 * What stays here is the part that is Hub's: binding the row shapes to the
 * CODEGEN entity types from `@/src/gql/graphql`, not hand-rolled interfaces —
 * the read/display half of the rule stated in SLACK_FORMATTERS_USE_CODEGEN_TYPES.
 * This is the single place every inherited-method surface reads these columns,
 * so it is also the single place a schema drift can be caught: rename or drop
 * `organization_phone` and `Pick` fails to compile HERE, at the binding, instead
 * of silently resolving nothing at runtime because a structural interface still
 * described the old shape. Run `npm run codegen` after any `.graphql` change so
 * these stay current.
 *
 * `Partial<>` wraps the pick deliberately. Surfaces load different subsets of a
 * contact or company (a list row carries less than a detail header), and the
 * resolver already treats every field as optional. Partial keeps those callers
 * legal while `Pick` still binds every field NAME to the generated schema —
 * leniency about which fields arrive, zero leniency about what they are called.
 */

import {
  resolveInheritedContactMethods as resolveInheritedContactMethodsShared,
  type InheritedContactMethods,
} from "@west-tennessee-consulting/contact-management/inherited-methods";
import type { Companies, Contacts } from "@/src/gql/graphql";

export {
  COMPANY_LINE_BADGE_LABEL,
  inheritedMethodTooltip,
  inheritedMethodEntityContext,
} from "@west-tennessee-consulting/contact-management/inherited-methods";

export type {
  InheritedContactMethod,
  InheritedContactMethods,
  InheritedMethodSource,
} from "@west-tennessee-consulting/contact-management/inherited-methods";

export type ContactOwnMethods = Partial<
  Pick<Contacts, "phone" | "phone_aliases" | "email" | "email_aliases" | "is_company_line">
>;

export type CompanySharedMethods = Partial<
  Pick<
    Companies,
    | "id"
    | "name"
    | "organization_phone"
    | "phone_aliases"
    | "organization_email"
    | "email_aliases"
    | "line_contact_id"
  >
>;

/**
 * Which of the company's shared methods this person inherits.
 *
 * Typed against the codegen shapes above; the resolution is the shared one.
 * Passing them through here is what makes a schema rename a compile error at
 * this binding rather than a silent empty result at runtime.
 */
export function resolveInheritedContactMethods(
  contact: ContactOwnMethods | null | undefined,
  company: CompanySharedMethods | null | undefined
): InheritedContactMethods {
  return resolveInheritedContactMethodsShared(contact, company);
}
